import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { inventoryService } from '../../services/inventoryService';
import { useInventoryFilters } from '../useInventoryFilters';
import { useSort } from '../useSort';
import { usePaginationState } from '../usePaginationState';
import { useSettingsStore } from '../../store/settingsStore';
import { cache } from '../../utils/cache';
import type { InventoryItem } from '../../types/inventory';
import { useLanguage } from '../../features/language/useLanguage';
import { usePermissions } from '../usePermissions';
import { isForbiddenError } from '../../services/api';

export type InventoryTypeFilter = 'all' | 'green_bean' | 'roasted_bulk' | 'finished_good';

export interface RoastProfileContext {
  itemId: string;
  itemName: string;
  workOrderId?: string;
}

export interface FinishedGoodsTasteProfileContext {
  item: InventoryItem;
  mode: 'add' | 'view';
  existingProfileId?: string;
}

export const useInventoryPageState = () => {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const { currencySymbol } = useSettingsStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [editingItem, setEditingItem] = useState<InventoryItem | undefined>(undefined);
  const [historyItemId, setHistoryItemId] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isFinishedGoodsModalOpen, setIsFinishedGoodsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [isImportRBModalOpen, setIsImportRBModalOpen] = useState(false);
  const [expandedRowIds, setExpandedRowIds] = useState<Set<string>>(new Set());
  const [roastProfileContext, setRoastProfileContext] = useState<RoastProfileContext | null>(null);
  const [tasteProfileContext, setTasteProfileContext] = useState<FinishedGoodsTasteProfileContext | null>(null);
  const [tasteProfileListItem, setTasteProfileListItem] = useState<InventoryItem | null>(null);
  const [typeFilter, setTypeFilter] = useState<InventoryTypeFilter>(() => {
    const initial = searchParams.get('type') as InventoryTypeFilter | null;
    if (initial === 'green_bean' || initial === 'roasted_bulk' || initial === 'finished_good' || initial === 'all') {
      return initial;
    }
    return 'green_bean';
  });

  const { can } = usePermissions();
  const canReadInventory = can('inventory', 'read');

  const { data: itemsData, isLoading: loading, error: queryError } = useQuery({
    queryKey: ['inventory'],
    queryFn: () => inventoryService.getAll(),
    enabled: canReadInventory,
    retry: (failureCount, err) => !isForbiddenError(err) && failureCount < 3,
  });

  const items = useMemo(() => itemsData || [], [itemsData]);
  const isForbidden = !canReadInventory || isForbiddenError(queryError);
  const error = queryError && !isForbidden
    ? 'Failed to load inventory items. Please try again.'
    : null;

  const {
    nameFilter,
    setNameFilter,
    statusFilter,
    setStatusFilter,
    stockStatusFilter,
    setStockStatusFilter,
    filteredItems: filteredList,
  } = useInventoryFilters(items);

  const typeFilteredItems = useMemo(() => {
    if (typeFilter === 'all') {
      return filteredList;
    }
    return filteredList.filter(item => item.itemType === typeFilter);
  }, [filteredList, typeFilter]);

  const { sortedItems: filteredItems, sortConfig, handleSort } = useSort(typeFilteredItems);

  const { page, size, offset, setPage, setSize, resetOnChange } = usePaginationState({ defaultSize: 25 });
  const totalFiltered = filteredItems.length;
  const paginatedItems = useMemo(
    () => filteredItems.slice(offset, offset + size),
    [filteredItems, offset, size]
  );
  resetOnChange([nameFilter, statusFilter, stockStatusFilter, typeFilter, sortConfig?.key, sortConfig?.direction]);

  const toggleRow = (id: string) => {
    setExpandedRowIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  useEffect(() => {
    const openId = searchParams.get('openId');
    if (openId && items.length > 0) {
      const itemToOpen = items.find(i => i.id === openId);
      if (itemToOpen) {
        setTimeout(() => {
          setEditingItem(itemToOpen);
          setIsCreateModalOpen(true);
        }, 0);
      }
    }
  }, [searchParams, items]);

  useEffect(() => {
    const current = searchParams.get('type') || 'all';
    if (current === typeFilter) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    if (typeFilter === 'all') {
      next.delete('type');
    } else {
      next.set('type', typeFilter);
    }
    setSearchParams(next, { replace: true });
  }, [typeFilter, searchParams, setSearchParams]);

  const handleDelete = async (id: string) => {
    if (!window.confirm(t('inventory.page.confirmDeleteItem'))) return;
    try {
      await inventoryService.delete(id);
      await queryClient.invalidateQueries({ queryKey: ['inventory'] });
    } catch (err: unknown) {
      const errorMessage =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (err as Error).message ||
        t('errors.common.unknownError');
      alert(`${t('inventory.page.failedDeleteItemPrefix')}${errorMessage}`);
    }
  };

  const handleRestock = (item: InventoryItem) => {
    navigate('/purchase-orders', {
      state: {
        createPO: true,
        initialItem: {
          inventoryItemId: item.id,
          name: item.name,
          code: item.code,
          quantity: Math.max(10, (item.reorderPoint || 0) - item.stock + 10),
          unitPrice: item.standardCost || 0,
        },
      },
    });
  };

  const handleEditItem = (itemId: string) => {
    const found = items.find(i => i.id === itemId);
    if (found) {
      setEditingItem(found);
      setIsCreateModalOpen(true);
    }
  };

  const formatDate = (dateString: string | Date) => {
    const date = new Date(dateString);
    const time = new Intl.DateTimeFormat('en-GB', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: 'Asia/Jakarta',
    }).format(date);
    const day = new Intl.DateTimeFormat('en-GB', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
      timeZone: 'Asia/Jakarta',
    }).format(date);
    return `${time}, ${day}`;
  };

  const handleInventoryRefresh = async () => {
    cache.invalidate('inventory');
    await queryClient.invalidateQueries({ queryKey: ['inventory'] });
  };

  return {
    currencySymbol,
    items,
    loading,
    error,
    isForbidden,
    typeFilter,
    setTypeFilter,
    nameFilter,
    setNameFilter,
    statusFilter,
    setStatusFilter,
    stockStatusFilter,
    setStockStatusFilter,
    filteredItems,
    paginatedItems,
    totalFiltered,
    page,
    pageSize: size,
    setPage,
    setPageSize: setSize,
    sortConfig,
    handleSort,
    expandedRowIds,
    toggleRow,
    editingItem,
    setEditingItem,
    historyItemId,
    setHistoryItemId,
    isCreateModalOpen,
    setIsCreateModalOpen,
    isFinishedGoodsModalOpen,
    setIsFinishedGoodsModalOpen,
    isImportModalOpen,
    setIsImportModalOpen,
    isImportRBModalOpen,
    setIsImportRBModalOpen,
    roastProfileContext,
    setRoastProfileContext,
    tasteProfileContext,
    setTasteProfileContext,
    tasteProfileListItem,
    setTasteProfileListItem,
    handleDelete,
    handleRestock,
    handleEditItem,
    formatDate,
    handleInventoryRefresh,
  };
};
